/**
 * Company Map tab: every saved Company Map run that matched this person,
 * newest first — the Company Map counterpart of the AI Review tab.
 *
 * Each card says what matched (role at company, years), what was searched
 * and the roles to track. For viewers allowed to see model calls
 * (`usePromptViewerAllowed`) it also carries the run's model calls as tabs
 * (Categories / Assign / Details), and "Open full screen" for
 * `CompanyMapPromptModal`, on the batch the inline tabs show.
 *
 * The modal is portalled to `<body>` and the host suspends the panel while it
 * is open (`onModalChange`), as on the AI Review tab.
 */
import type { PersonCompanyMapRun, PersonDetail } from "@nu-assist/contracts";
import { Button } from "@nu-assist/ui";
import { Maximize2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { type BctClient, createBctClient } from "../../clients/bct.client";
import { usePromptViewerAllowed } from "../ai-review-prompt/access";
import { CompanyMapCallTabs } from "../company-map-prompt/CompanyMapCallTabs";
import { CompanyMapPromptModal } from "../company-map-prompt/CompanyMapPromptModal";
import { EmptyLine, PanelSection } from "./PanelSection";
import { fmtDate, fmtDateTime, personFirstName } from "./format";
import { useSeenOnce } from "./useSeenOnce";

const defaultClient = createBctClient();

function yearRange(start?: number | null, end?: number | null): string {
  if (start == null && end == null) return "";
  return `${start ?? "?"}–${end ?? "present"}`;
}

/** What a run matched and searched — shared with the Profile tab's section. */
export function CompanyMapRunSummary({ run }: { run: PersonCompanyMapRun }) {
  return (
    <>
      {(run.matches ?? []).map((m, i) => (
        <div key={`${m.company_name}-${i}`} data-testid="person-company-map-match">
          Matched <span className="font-medium">{m.job_title ?? "a role"}</span> at{" "}
          <span className="font-medium">{m.company_name ?? "an unnamed company"}</span>
          {yearRange(m.start_year, m.end_year) && ` (${yearRange(m.start_year, m.end_year)})`}
        </div>
      ))}
      {(run.companies ?? []).length > 0 && (
        <div className="text-caption text-text-secondary">
          Searched:{" "}
          {(run.companies ?? [])
            .map((c) =>
              [c.name, c.role, yearRange(c.start_year, c.end_year)].filter(Boolean).join(" · ")
            )
            .join("; ")}
        </div>
      )}
      {run.roles_description && (
        <div className="text-caption text-text-secondary" data-testid="person-company-map-roles">
          Roles to track: {run.roles_description}
        </div>
      )}
    </>
  );
}

/** The short date line the Profile section shows. */
export function companyMapRunLine(run: PersonCompanyMapRun): string {
  return `${fmtDate(run.created_at)}${run.triggered_by ? ` · run by ${run.triggered_by}` : ""}`;
}

function matchedTitles(run: PersonCompanyMapRun): string[] {
  return (run.matches ?? []).map((m) => m.job_title ?? "").filter(Boolean);
}

/** The run's model calls as tabs, and "Open full screen". The latest run
 *  reads its calls at once; an older one when it scrolls into view
 *  (`useSeenOnce`), so a person with many runs does not rebuild every one. */
function ModelCalls({
  run,
  client,
  eager,
  onOpen,
}: {
  run: PersonCompanyMapRun;
  client: BctClient;
  eager: boolean;
  onOpen: (run: PersonCompanyMapRun, batch: number | null) => void;
}) {
  const batch = useRef<number | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const seen = useSeenOnce(ref, eager);
  return (
    <div
      ref={ref}
      className="flex flex-col gap-3 rounded-md border border-border bg-bg p-3"
      data-testid="company-map-model-calls"
    >
      {seen ? (
        <CompanyMapCallTabs
          client={client}
          runId={run.run_id}
          titles={matchedTitles(run)}
          onBatchChange={(b) => {
            batch.current = b;
          }}
        />
      ) : (
        <p
          className="py-6 text-caption text-text-muted"
          data-testid="company-map-model-calls-waiting"
        >
          The model calls load when this card is on screen.
        </p>
      )}
      <div>
        <Button
          type="button"
          variant="secondary"
          data-testid="company-map-open-full-screen"
          data-run-id={run.run_id}
          onClick={() => onOpen(run, batch.current)}
        >
          <Maximize2 className="h-4 w-4" aria-hidden="true" />
          Open full screen
        </Button>
      </div>
    </div>
  );
}

function RunCard({
  run,
  latest,
  client,
  allowed,
  onOpen,
}: {
  run: PersonCompanyMapRun;
  latest: boolean;
  client: BctClient;
  allowed: boolean;
  onOpen: (run: PersonCompanyMapRun, batch: number | null) => void;
}) {
  return (
    <article
      className={`flex flex-col gap-2 rounded-lg border bg-surface ${
        latest ? "border-accent p-4" : "border-border p-3"
      }`}
      data-testid="company-map-run-card"
      data-latest={latest ? "true" : "false"}
      data-run-id={run.run_id}
    >
      <div
        className="text-micro font-semibold uppercase tracking-wide text-text-muted"
        data-testid="company-map-run-header"
      >
        Company Map · {fmtDateTime(run.created_at)} · by {run.triggered_by ?? "—"}
        {latest ? " · Latest" : ""}
      </div>
      <div className="flex flex-col gap-1 text-body text-text">
        <CompanyMapRunSummary run={run} />
      </div>
      {allowed && <ModelCalls run={run} client={client} eager={latest} onOpen={onOpen} />}
    </article>
  );
}

export function CompanyMapTab({
  person,
  client = defaultClient,
  onModalChange,
}: {
  person: PersonDetail;
  client?: BctClient;
  /** The full-screen viewer opened / closed, in the same render as the viewer. */
  onModalChange?: (open: boolean) => void;
}) {
  const runs = person.company_map ?? [];
  const allowed = usePromptViewerAllowed();
  const [open, setOpen] = useState<{ run: PersonCompanyMapRun; batch: number | null } | null>(null);

  // Same handshake as AiReviewTab: tell the host in the same handler that
  // mounts the viewer, and never leave it suspended on unmount.
  const modalChange = useRef(onModalChange);
  modalChange.current = onModalChange;
  const openRef = useRef(false);
  const show = (next: { run: PersonCompanyMapRun; batch: number | null } | null) => {
    setOpen(next);
    if (openRef.current !== (next !== null)) {
      openRef.current = next !== null;
      modalChange.current?.(next !== null);
    }
  };
  useEffect(
    () => () => {
      if (openRef.current) modalChange.current?.(false);
    },
    []
  );

  if (runs.length === 0) {
    return (
      <PanelSection title="Company Map" testId="company-map-tab">
        <EmptyLine testId="company-map-tab-empty">
          No Company Map run has matched {personFirstName(person)} yet.
        </EmptyLine>
      </PanelSection>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="company-map-tab">
      {runs.map((run, i) => (
        <RunCard
          key={run.run_id}
          run={run}
          latest={i === 0}
          client={client}
          allowed={allowed}
          onOpen={(r, batch) => show({ run: r, batch })}
        />
      ))}
      {open &&
        createPortal(
          <CompanyMapPromptModal
            client={client}
            source={{ kind: "run", id: open.run.run_id }}
            // Step 0 is the categories call; batch n is step n + 1. No batch
            // (no titles, or not loaded yet) opens on the categories call.
            initialIndex={open.batch == null ? 0 : open.batch + 1}
            onClose={() => show(null)}
          />,
          document.body
        )}
    </div>
  );
}
